import type { GetAccountUsageQuery } from '@frameio/graphql-client/generated';
import { date as formatDate } from '@frameio/i18n';
import { getIsArchivalStorageIncluded } from '@frameio/plan-management/plans/utils';
import TEXT from './generated/TEXT/ArchivalStorageAlertBanner';
import { StorageAlertBannerBase } from './StorageAlertBannerBase';
import { SupportButton } from './SupportButton';
import { UniversalLink } from './UniversalLink';

TEXT.DEFINE(
  {
    /** Title for billing managers */
    titleWithPermission: (
      archivedStorageCount: string,
      archivedStorageLimit: string
    ) =>
      `Archival Storage Limit Exceeded (${archivedStorageCount} of ${archivedStorageLimit})`,

    /** Title for non-billing users */
    titleNoPermission: 'Archival Storage Limit Exceeded',

    /** Message for Adobe-managed accounts without billing permissions */
    detailsNoPermissionAdobe: 'Contact your admin for assistance.',

    /** Message for non-billing users with grace period */
    detailsNoPermissionWithGrace: (ownerEmail: string, graceDate: Date) => (
      <>
        Contact account owner{' '}
        <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </UniversalLink>{' '}
        for assistance before {formatDate(graceDate, 'medium')} or account will
        be locked.
      </>
    ),

    /** Message for non-billing users without grace period */
    detailsNoPermissionNoGrace: (ownerEmail: string) => (
      <>
        Contact account owner{' '}
        <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </UniversalLink>{' '}
        for assistance.
      </>
    ),

    /** Message for Enterprise non-billing users */
    detailsNoPermissionEnterprise: (ownerEmail: string) => (
      <>
        Contact account owner{' '}
        <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </UniversalLink>{' '}
        for assistance.
      </>
    ),

    /** Message for Enterprise billing managers */
    detailsWithPermissionEnterprise: (manageStorageLink: string) => (
      <>
        <UniversalLink href={manageStorageLink}>
          Manage storage usage
        </UniversalLink>{' '}
        or <SupportButton>contact support</SupportButton> for assistance.
      </>
    ),

    /** Message for billing managers with grace period */
    detailsWithPermissionWithGrace: (
      manageStorageLink: string,
      graceDate: Date
    ) => (
      <>
        <UniversalLink href={manageStorageLink}>
          Manage storage usage
        </UniversalLink>{' '}
        before {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    /** Message for billing managers without grace period */
    detailsWithPermissionNoGrace: (manageStorageLink: string) => (
      <>
        <UniversalLink href={manageStorageLink}>
          Manage storage usage
        </UniversalLink>
        .
      </>
    ),
  },
  { UniversalLink, SupportButton }
);

type ArchivalStorageAlertBannerProps = {
  account: NonNullable<GetAccountUsageQuery['account']>;
};

export function ArchivalStorageAlertBanner({
  account,
}: ArchivalStorageAlertBannerProps) {
  return (
    <StorageAlertBannerBase
      account={account}
      featureGateKey="ARCHIVAL_STORAGE"
      getIsStorageAddOnIncluded={getIsArchivalStorageIncluded}
      storageBytes={
        account.subscription?.limitedResources?.archivedStorageBytes
      }
      text={TEXT}
    />
  );
}
